import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../api/axios";
import useProfileCard from "../hooks/useProfileCard";
import ParticipantCardPreview from "../pages/special/sandbox/ParticipantCardPreview";
import ReportForm from "../components/search/ReportForm";
import { buildAuthRequiredPath } from "../utils/authRedirect";

const ProfileCardContext = createContext({ openProfileCard: () => {}, isProfileCardOpen: false });
export const useProfileCardModal = () => useContext(ProfileCardContext);

export function ProfileCardProvider({ children, isGuest = false }) {
  const card = useProfileCard();
  const { open, close } = card;
  const [reportKind, setReportKind] = useState(null);
  const [badPhotoSubmitting, setBadPhotoSubmitting] = useState(false);
  const badPhotoRequestRef = useRef(false);
  const [openingVault, setOpeningVault] = useState(false);
  const [ratingRole, setRatingRole] = useState("participant");
  const [eventContactContext, setEventContactContext] = useState(null);
  const vaultRequestRef = useRef(0);
  const returnToRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("search");

  const closeProfileCard = useCallback(() => {
    vaultRequestRef.current += 1;
    setOpeningVault(false);
    close();
    setReportKind(null);
    setRatingRole("participant");
    setEventContactContext(null);
  }, [close]);
  useEffect(() => { closeProfileCard(); }, [location.pathname, closeProfileCard]);
  const openProfileCard = useCallback((person, options = {}) => {
    if (isGuest) {
      navigate(buildAuthRequiredPath({ next: `${location.pathname}${location.search}` }));
      return;
    }
    vaultRequestRef.current += 1;
    setOpeningVault(false);
    setReportKind(null);
    setRatingRole(options.ratingRole === "host" ? "host" : "participant");
    setEventContactContext(options.eventContactContext || null);
    returnToRef.current = options.returnTo || `${location.pathname}${location.search}`;
    void open(person);
  }, [isGuest, location.pathname, location.search, navigate, open]);
  useEffect(() => {
    if (isGuest || location.pathname !== "/search/main") return;
    const params = new URLSearchParams(location.search);
    const code = params.get("profile_code")?.trim().toUpperCase();
    if (!/^[A-Z0-9]{3}$/.test(code || "")) return;
    openProfileCard(code, { returnTo: "/search/main" });
    params.delete("profile_code");
    const remainingQuery = params.toString();
    navigate(`/search/main${remainingQuery ? `?${remainingQuery}` : ""}`, { replace: true });
  }, [isGuest, location.pathname, location.search, navigate, openProfileCard]);
  const isProfileCardOpen = Boolean(card.participant);
  const contextValue = useMemo(() => ({ openProfileCard, isProfileCardOpen }), [openProfileCard, isProfileCardOpen]);

  const openVault = async (code) => {
    if (openingVault) return;
    const requestId = ++vaultRequestRef.current;
    const returnTo = returnToRef.current;
    setOpeningVault(true);
    try {
      const [profile, diamonds, keys, storage] = await Promise.all([
        api.get("/accounts/profile/"),
        api.get("/storage/user_diamonds/"),
        api.get("/storage/user_keys/"),
        api.post("/storage/storage_search/", { search_code: code }),
      ]);
      if (vaultRequestRef.current !== requestId) return;
      closeProfileCard();
      navigate(`/search/vault/${code}`, { state: {
        profile: profile.data, diamonds: diamonds.data.diamonds,
        keys: keys.data, storageData: storage.data,
        returnTo,
      } });
    } catch (error) {
      if (vaultRequestRef.current !== requestId) return;
      window.dispatchEvent(new CustomEvent("global-error", { detail: {
        status: error?.response?.status,
        message: error?.response?.data?.error || t("ranking.search_failed", { ns: "vault", defaultValue: "Could not open Vault" }),
      } }));
    } finally {
      if (vaultRequestRef.current === requestId) setOpeningVault(false);
    }
  };
  const openEventDirectMessage = (code) => {
    if (!eventContactContext?.id || !code) return;
    const peer = {
      code,
      username: card.participant?.username || "",
      avatar_url: card.participant?.avatar_url || null,
      avatar_custom_url: card.participant?.avatar_custom_url || null,
    };
    const eventContext = eventContactContext;
    closeProfileCard();
    navigate(`/menu/chats/direct/code/${encodeURIComponent(code)}`, {
      state: { source: "event_profile_card", peer, eventContext },
    });
  };
  const submitReport = async (message) => {
    await api.post("/accounts/report-user/", {
      code: card.participant.code,
      description: message,
    });
    setReportKind(null);
    window.dispatchEvent(new CustomEvent("global-info", { detail: {
      message: t("report_form.success", { defaultValue: "Report sent" }),
    } }));
  };
  const submitBadPhoto = async () => {
    if (badPhotoRequestRef.current || !card.participant?.code) return;
    badPhotoRequestRef.current = true;
    setBadPhotoSubmitting(true);
    try {
      const { data } = await api.post("/accounts/report-bad-photo/", { code: card.participant.code });
      window.dispatchEvent(new CustomEvent("global-info", { detail: {
        message: data?.status === "already_reported"
          ? t("report_form.photo_already_sent")
          : t("report_form.photo_sent"),
      } }));
    } catch (error) {
      window.dispatchEvent(new CustomEvent("global-error", { detail: {
        status: error?.response?.status,
        message: error?.response?.status === 429
          ? t("report_form.photo_limit")
          : error?.response?.data?.error || t("report_form.error"),
      } }));
    } finally {
      badPhotoRequestRef.current = false;
      setBadPhotoSubmitting(false);
    }
  };

  return <ProfileCardContext.Provider value={contextValue}>
    {children}
    <ParticipantCardPreview open={Boolean(card.participant)} participant={card.participant}
      ratingRole={ratingRole}
      profileLoading={card.loading} profileError={card.error} onRetry={card.retry}
      vaultOpening={openingVault} actionsOverlayOpen={Boolean(reportKind) || badPhotoSubmitting}
      onClose={closeProfileCard} onOpenVault={openVault}
      onMessage={eventContactContext ? openEventDirectMessage : undefined}
      onReport={() => setReportKind("user")} onBadPhoto={submitBadPhoto} />
    <ReportForm isOpen={Boolean(card.participant && reportKind)} mode="user"
      onClose={() => setReportKind(null)} onSubmit={submitReport} />
  </ProfileCardContext.Provider>;
}
